/** Catálogo de tipos de servicio y utilidades para el informe de servicios. */

export const SERVICE_CATEGORIES = [
  { value: "preventivo", labelKey: "categoryPreventive" },
  { value: "correctivo", labelKey: "categoryCorrective" },
  { value: "instalacion", labelKey: "categoryInstallation" },
];

export const SERVICE_TYPES = [
  { value: "preventivo", labelKey: "preventivo_ascensor", category: "preventivo" },
  { value: "correctivo", labelKey: "correctivo_ascensor", category: "correctivo" },
  { value: "instalacion", labelKey: "instalacion_ascensor", category: "instalacion" },
  {
    value: "electric_plant",
    labelKey: "preventivo_planta_electrica",
    category: "preventivo",
  },
  {
    value: "electric_plant_correctivo",
    labelKey: "correctivo_planta_electrica",
    category: "correctivo",
  },
  {
    value: "electric_plant_instalacion",
    labelKey: "instalacion_planta_electrica",
    category: "instalacion",
  },
  {
    value: "vehicular_door",
    labelKey: "preventivo_puerta_vehicular",
    category: "preventivo",
  },
  {
    value: "vehicular_door_correctivo",
    labelKey: "correctivo_puerta_vehicular",
    category: "correctivo",
  },
  {
    value: "vehicular_door_instalacion",
    labelKey: "instalacion_puerta_vehicular",
    category: "instalacion",
  },
  {
    value: "escalator",
    labelKey: "preventivo_escalera_electrica",
    category: "preventivo",
  },
  {
    value: "escalator_correctivo",
    labelKey: "correctivo_escalera_electrica",
    category: "correctivo",
  },
  {
    value: "escalator_instalacion",
    labelKey: "instalacion_escalera_electrica",
    category: "instalacion",
  },
];

export const SERVICE_STATUSES = [
  "pending",
  "assigned",
  "in_progress",
  "completed",
  "out_of_service",
];

const SERVICE_TYPES_BY_VALUE = SERVICE_TYPES.reduce((acc, type) => {
  acc[type.value] = type;
  return acc;
}, {});

export const getServiceTypeLabel = (t, value) => {
  const type = SERVICE_TYPES_BY_VALUE[value];
  return type ? t(type.labelKey) : value || "";
};

export const getServiceCategory = (value) =>
  SERVICE_TYPES_BY_VALUE[value]?.category || "";

export const getServiceCategoryLabel = (t, value) => {
  const category = SERVICE_CATEGORIES.find(
    (item) => item.value === getServiceCategory(value)
  );
  return category ? t(category.labelKey) : "";
};

const firstValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

export const toDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date;
};

export const formatDateTime = (value) => {
  const date = toDate(value);
  return date ? date.toLocaleString() : "";
};

export const formatDate = (value) => {
  const date = toDate(value);
  return date ? date.toLocaleDateString() : "";
};

/** Notas del checklist asociadas a una pregunta concreta (observaciones, etc.). */
const getChecklistNotes = (schedule, questionName) => {
  const checklists = schedule?.schedule_checklists;
  const questions = schedule?.checklist_questions;
  if (!Array.isArray(checklists) || !Array.isArray(questions)) return "";

  return checklists
    .filter((checklist) => {
      const question = questions.find(
        (item) =>
          item.id === checklist.checklist_question_id &&
          item.question === questionName
      );
      return (
        question?.service_type === schedule.service_type && !!checklist.notes
      );
    })
    .map((checklist) => checklist.notes)
    .join(" | ");
};

const getDurationHours = (schedule) => {
  const start = toDate(schedule?.service_started_at);
  const end = toDate(schedule?.service_completed_at);
  if (!start || !end || end < start) return "";
  return Math.round(((end - start) / 3600000) * 100) / 100;
};

/**
 * Normaliza un schedule (venga del listado liviano o del detalle) a la fila
 * que se muestra en la tabla y se exporta al Excel.
 */
export const buildReportRow = (schedule, { t, clientsById, usersById }) => {
  const client = clientsById?.[schedule.client_id];
  const technician = usersById?.[schedule.user_id];
  const building = schedule.elevator?.building;

  return {
    id: schedule.id,
    startDate: toDate(schedule.start_time),
    serviceType: getServiceTypeLabel(t, schedule.service_type),
    serviceCategory: getServiceCategoryLabel(t, schedule.service_type),
    status: schedule.status ? t(schedule.status) : "",
    client:
      firstValue(schedule.client_name, schedule.client?.name, client?.name) || "",
    technician:
      firstValue(schedule.user_name, schedule.user?.name, technician?.name) || "",
    description: schedule.description || "",
    location:
      firstValue(schedule.location_name, building?.location?.name) || "",
    address: firstValue(schedule.address, building?.location?.address) || "",
    building: firstValue(schedule.building_name, building?.name) || "",
    elevator:
      firstValue(
        schedule.elevator_identifier,
        schedule.elevator?.identifier,
        schedule.elevator_id ? `#${schedule.elevator_id}` : null
      ) || "",
    startedAt: toDate(schedule.service_started_at),
    completedAt: toDate(schedule.service_completed_at),
    durationHours: getDurationHours(schedule),
    observations: getChecklistNotes(schedule, "observaciones"),
    workDone: getChecklistNotes(schedule, "trabajos realizados"),
    receivedBy: schedule.person_name || "",
    receivedByDocument: schedule.document || "",
  };
};

/** Columnas del Excel. Las de detalle solo se incluyen si se pidió el detalle. */
export const getReportColumns = (t, { includeDetails }) => {
  const baseColumns = [
    { key: "id", header: "ID", width: 8 },
    { key: "startDate", header: t("scheduledDate"), width: 18 },
    { key: "serviceType", header: t("serviceType"), width: 26 },
    { key: "serviceCategory", header: t("serviceCategory"), width: 16 },
    { key: "status", header: t("status"), width: 14 },
    { key: "client", header: t("client"), width: 28 },
    { key: "technician", header: t("technician"), width: 24 },
    { key: "description", header: t("description"), width: 40, wrap: true },
  ];

  if (!includeDetails) return baseColumns;

  return [
    ...baseColumns,
    { key: "location", header: t("location"), width: 24 },
    { key: "address", header: t("address"), width: 28 },
    { key: "building", header: t("building"), width: 24 },
    { key: "elevator", header: t("elevator"), width: 16 },
    { key: "startedAt", header: t("serviceStarted"), width: 18 },
    { key: "completedAt", header: t("serviceCompleted"), width: 18 },
    { key: "durationHours", header: t("durationHours"), width: 12 },
    { key: "workDone", header: t("workDone"), width: 45, wrap: true },
    { key: "observations", header: t("observations"), width: 45, wrap: true },
    { key: "receivedBy", header: t("receivedBy"), width: 24 },
    { key: "receivedByDocument", header: t("document"), width: 16 },
  ];
};

/** Nombre de archivo del tipo informe_servicios_2026-01-01_2026-01-31.xlsx */
export const buildFileName = ({ from, to }) =>
  `informe_servicios_${from || "inicio"}_${to || "fin"}`;
